// Pure display/filter helpers for the Campaigns tab (no React, no IO) —
// separated from the component so the logic is unit-testable and the tab file
// stays presentational.

import {
  type CampaignAudience,
  type CampaignChannel,
  type CampaignDisplayStatus,
  type CampaignStats,
  type CampaignStatus,
  type CampaignType,
  type FlowDelayUnit,
  type LoyaltyCampaign,
  type LoyaltyCampaignHistoryEvent,
  type LoyaltyFlow,
  displayStatusForCampaign,
  displayStatusForFlow,
  flowTriggerLabel,
  normalizeCampaignType,
} from '../../../types/loyaltyMarketing'
import type { LoyaltySegment } from '../../../types/loyaltySegments'

// Type filter: a v2 type, or 'flows' for every always-on automation.
export type CampaignTypeFilter = 'all' | CampaignType | 'flows'

// Segment filter (mockup "All Segments" select): 'all' = no filter,
// 'all-customers' = campaigns sent to everyone, otherwise a segment id.
export type CampaignSegmentFilter = 'all' | 'all-customers' | string

export interface CampaignFilterState {
  // Substring match (case-insensitive) against name, subject and SMS body.
  search: string
  type: CampaignTypeFilter
  // 'all' or a display status (running/completed — not sending/sent).
  status: 'all' | CampaignDisplayStatus
  segment: CampaignSegmentFilter
}

const audienceSegmentIds = (
  audience: CampaignAudience | undefined,
): string[] =>
  audience?.segment_ids?.length
    ? audience.segment_ids
    : audience?.segment_id
      ? [audience.segment_id]
      : []

export const matchesSegmentFilter = (
  audience: CampaignAudience | undefined,
  filter: CampaignSegmentFilter,
): boolean => {
  if (filter === 'all') return true
  if (filter === 'all-customers') return !audience || audience.type === 'all'
  return (
    audience?.type === 'segment' &&
    audienceSegmentIds(audience).includes(filter)
  )
}

// Display type for a stored campaign — the API's `display_type` when
// present, else legacy bonus/winback/… rows read as 'email' (spec §1.1);
// older docs without a `type` default the same way.
export const campaignType = (campaign: LoyaltyCampaign): CampaignType =>
  normalizeCampaignType(campaign.type, campaign.channel, campaign.display_type)

// Same rollout story for `channel`: older docs omit it and the server
// defaults to 'email', so mirror that wherever channel is branched.
export const campaignChannel = (campaign: LoyaltyCampaign): CampaignChannel =>
  campaign.channel ?? 'email'

// A Smart send time campaign — the server spreads it over up to 24 hourly
// buckets instead of firing at one timestamp, so it has NO meaningful
// `scheduled_at` and every "when does this go out" surface has to say so.
export const isSmartSendCampaign = (campaign: LoyaltyCampaign): boolean =>
  campaign.schedule_mode === 'smart'

// The label every surface uses in place of a timestamp for a smart campaign
// (list Date cell, drawer Send time row) so they can never disagree.
export const SMART_SEND_LABEL = 'Smart send time'

export const filterCampaigns = (
  campaigns: LoyaltyCampaign[],
  filter: CampaignFilterState,
): LoyaltyCampaign[] => {
  // "Always-on flows" is a flow-only bucket.
  if (filter.type === 'flows') return []
  const search = filter.search.trim().toLowerCase()
  return campaigns.filter((c) => {
    if (search) {
      const haystack =
        `${c.name ?? ''}\n${c.subject ?? ''}\n${c.sms_body ?? ''}`.toLowerCase()
      if (!haystack.includes(search)) return false
    }
    if (filter.type !== 'all' && campaignType(c) !== filter.type) return false
    if (
      filter.status !== 'all' &&
      displayStatusForCampaign(c) !== filter.status
    )
      return false
    return matchesSegmentFilter(c.audience, filter.segment)
  })
}

// Human-readable delay summary, e.g. (4, 'hours') -> "4 hours",
// (1, 'days') -> "1 day", (0, *) -> "Immediately".
export const formatDelay = (value: number, unit: FlowDelayUnit): string => {
  if (!value || value <= 0) return 'Immediately'
  const label = value === 1 ? unit.replace(/s$/, '') : unit
  return `${value} ${label}`
}

// Description line for a flow row in the unified Campaigns list, e.g.
// "Always-on · Member's birthday" or "Always-on · Placed Order +1 day".
export const flowRowDescription = (flow: LoyaltyFlow): string => {
  const trigger = flowTriggerLabel(flow.trigger_metric)
  const delay =
    flow.delay_value > 0
      ? ` +${formatDelay(flow.delay_value, flow.delay_unit)}`
      : ''
  const channel = flow.channel === 'sms' ? 'SMS · ' : ''
  return `Always-on · ${channel}${trigger}${delay}`
}

// Which Type bucket a flow belongs to: abandoned-cart automations get their
// own tag (mockup `cart`), SMS flows read as SMS, everything else is a plain
// always-on flow.
export const flowListType = (
  flow: LoyaltyFlow,
): 'abandoned_cart' | 'sms' | 'flow' =>
  flow.trigger_metric === 'abandoned_cart'
    ? 'abandoned_cart'
    : flow.channel === 'sms'
      ? 'sms'
      : 'flow'

export interface FlowFilterState {
  // Substring match (case-insensitive) against name and trigger label.
  search: string
  // 'all' or a display status (running — not live).
  status: 'all' | CampaignDisplayStatus
  type: CampaignTypeFilter
  segment: CampaignSegmentFilter
}

// Flow counterpart of filterCampaigns for the unified Campaigns list. Flows
// are auto-targeted, so any segment filter hides them; 'email' is a
// campaign-only bucket while 'abandoned_cart' / 'sms' match flows too.
export const filterFlows = (
  flows: LoyaltyFlow[],
  filter: FlowFilterState,
): LoyaltyFlow[] => {
  if (filter.segment !== 'all') return []
  const search = filter.search.trim().toLowerCase()
  return flows.filter((flow) => {
    if (search) {
      const haystack =
        `${flow.name ?? ''}\n${flowTriggerLabel(flow.trigger_metric)}`.toLowerCase()
      if (!haystack.includes(search)) return false
    }
    if (filter.status !== 'all' && displayStatusForFlow(flow) !== filter.status)
      return false
    if (filter.type === 'all' || filter.type === 'flows') return true
    return flowListType(flow) === filter.type
  })
}

// Percentage (1-decimal) of `numerator` over `denominator`; null when the
// denominator is 0/absent so callers can render an em-dash instead of a
// misleading 0%.
export const ratePercent = (
  numerator: number | undefined,
  denominator: number | undefined,
): number | null => {
  if (!denominator) return null
  return Math.round(((numerator ?? 0) / denominator) * 1000) / 10
}

// Whole-number percentage of `numerator` over `denominator` (the list's
// performance column, mirroring the mock's pct()); null when the denominator
// is 0/absent so callers can render an em-dash instead of a misleading 0%.
export const wholePercent = (
  numerator: number | undefined,
  denominator: number | undefined,
): number | null => {
  if (!denominator) return null
  return Math.round(((numerator ?? 0) / denominator) * 100)
}

// Performance-column model for a campaign row. 'unsent' until at least one
// message went out; SMS rows summarise Twilio deliveries; email rows carry the
// three Delivered / Opens / Clicks rates the bar chart renders.
export type CampaignPerformance =
  | { kind: 'unsent' }
  | {
      kind: 'sms'
      delivered: number
      // delivered / sent — null when `delivered` is untracked.
      deliveryRate: number | null
      conversions: number
    }
  | {
      kind: 'email'
      // delivered / sent — null when `delivered` is undefined (emails sent
      // before delivery tracking existed), rendered as "—" + empty bar.
      deliveredRate: number | null
      openRate: number | null
      clickRate: number | null
    }

// Performance model from a stats blob + channel. Shared by campaigns and
// event-triggered flows so both rows render the same Delivered / Opens /
// Clicks cell — a flow is a campaign whose sends are triggered one at a time.
export const performanceFromStats = (
  stats:
    | Pick<
        CampaignStats,
        'sent' | 'delivered' | 'opens' | 'clicks' | 'conversions'
      >
    | undefined,
  channel: CampaignChannel,
): CampaignPerformance => {
  const sent = stats?.sent ?? 0
  if (!sent || !stats) return { kind: 'unsent' }
  if (channel === 'sms') {
    return {
      kind: 'sms',
      delivered: stats.delivered ?? sent,
      deliveryRate:
        stats.delivered === undefined
          ? null
          : wholePercent(stats.delivered, sent),
      conversions: stats.conversions ?? 0,
    }
  }
  // Opens/clicks are rates over deliveries like the mock, but fall back to
  // rates over sends when `delivered` is undefined/0 — email campaigns sent
  // before delivery tracking (or whose deliveries all failed) would otherwise
  // show nothing at all.
  const engagementBase = stats.delivered || sent
  return {
    kind: 'email',
    deliveredRate:
      stats.delivered === undefined
        ? null
        : wholePercent(stats.delivered, sent),
    openRate: wholePercent(stats.opens, engagementBase),
    clickRate: wholePercent(stats.clicks, engagementBase),
  }
}

export const campaignPerformance = (
  campaign: LoyaltyCampaign,
): CampaignPerformance =>
  performanceFromStats(campaign.stats, campaignChannel(campaign))

// Revenue-column model: attributed revenue + conversions, or 'unknown' when
// nothing has been attributed yet (unsent, or sent before the ledger).
export type CampaignRevenue =
  | { kind: 'unknown' }
  | {
      kind: 'known'
      revenue: number
      conversions: number
      // conversions / (delivered || sent), whole %; null when nothing was sent.
      conversionPct: number | null
      final: boolean
    }

// Shared by campaigns and flows (spec §2.2): `$X` + `N conv.` straight from
// stats.revenue / stats.conversions, whatever the lifecycle status — a
// sending campaign or a live flow shows its running total.
export const revenueFromStats = (
  stats: CampaignStats | undefined,
): CampaignRevenue => {
  if (stats?.revenue === undefined) return { kind: 'unknown' }
  return {
    kind: 'known',
    revenue: stats.revenue,
    conversions: stats.conversions ?? 0,
    conversionPct: wholePercent(
      stats.conversions,
      stats.delivered || stats.sent,
    ),
    final: Boolean(stats.attribution_final),
  }
}

export const campaignRevenue = (campaign: LoyaltyCampaign): CampaignRevenue =>
  revenueFromStats(campaign.stats)

export const flowRevenue = (flow: LoyaltyFlow): CampaignRevenue =>
  revenueFromStats(flow.stats)

// Segment-column model for a campaign row: the audience label plus an
// optional size sub-line. `segmentById` resolves audience.type === 'segment'
// to the segment (name + current member_count); the label falls back to a
// generic "Segment" while segments load / if the segment was deleted.
export interface CampaignAudienceSummary {
  label: string
  size?: string
}

export const campaignAudienceSummary = (
  audience: CampaignAudience | undefined,
  segmentById?: (segmentId: string) => LoyaltySegment | undefined,
  // Known audience size for all-customer sends (stats.recipients once sent).
  knownSize?: number,
): CampaignAudienceSummary => {
  const everyone = (): CampaignAudienceSummary => ({
    label: 'All Customers',
    size: knownSize ? formatRecipients(knownSize) : undefined,
  })
  if (!audience) return everyone()
  switch (audience.type) {
    case 'percentage':
      return { label: `${audience.percentage ?? 0}% sample` }
    case 'ids':
      return {
        label: `${audience.customer_ids?.length ?? 0} selected customers`,
      }
    case 'segment': {
      // Multi-segment audiences list every target; legacy docs carry one id.
      const ids = audienceSegmentIds(audience)
      const found = ids
        .map((id) => segmentById?.(id))
        .filter((s): s is LoyaltySegment => Boolean(s))
      if (found.length === 0) return { label: 'Segment' }
      const members = found.reduce((sum, s) => sum + (s.member_count ?? 0), 0)
      return {
        label: found.map((s) => s.name).join(', '),
        size: `${formatRecipients(members)}${found.length > 1 ? ' (combined)' : ''}`,
      }
    }
    default:
      return everyone()
  }
}

const formatRecipients = (n: number): string =>
  `${n.toLocaleString('en-US')} recipients`

const PURCHASE_FILTER_LABELS: Record<string, string> = {
  any: 'Any customer',
  purchased: 'Purchased at least once',
  repeat_3: 'Purchased 3+ times',
  spent_200: 'Spent over $200',
  recent_30: 'Purchased in last 30 days',
}

const EXCLUDE_FILTER_LABELS: Record<string, string> = {
  recent_campaign_7d: 'Received a campaign in last 7 days',
}

// "Filters Applied" pills of the drawer Segment tab: the purchase filter, the
// exclusion (when set) and the opt-in rule every send enforces.
export const audienceFilterLabels = (
  audience: CampaignAudience | undefined,
  channel: CampaignChannel,
): string[] => {
  const labels: string[] = []
  const purchase = audience?.filters?.purchase ?? 'any'
  labels.push(PURCHASE_FILTER_LABELS[purchase] ?? purchase)
  const exclude = audience?.filters?.exclude
  if (exclude && exclude !== 'none')
    labels.push(EXCLUDE_FILTER_LABELS[exclude] ?? exclude)
  labels.push(
    channel === 'sms' ? 'SMS subscribers only' : 'Email subscribers only',
  )
  return labels
}

// Flow rows' Performance cell: "N triggered" + opens count · open % ·
// conversions. null when the flow has not triggered yet.
export const flowPerformanceSummary = (
  stats: CampaignStats | undefined,
  channel: CampaignChannel,
): { value: string; sub: string } | null => {
  const sent = stats?.sent ?? 0
  if (!sent) return null
  const converted = `${(stats?.conversions ?? 0).toLocaleString('en-US')} converted`
  const value = `${sent.toLocaleString('en-US')} triggered`
  if (channel === 'sms') return { value, sub: converted }
  const opens = stats?.opens ?? 0
  const pct = wholePercent(opens, stats?.delivered || sent)
  return {
    value,
    sub: `${opens.toLocaleString('en-US')} opens · ${pct === null ? '—' : `${pct}%`} open · ${converted}`,
  }
}

// "Created by" for the drawer: the actor of the `created` history event.
export const createdByFromHistory = (
  events: Pick<LoyaltyCampaignHistoryEvent, 'event' | 'actor'>[],
): string | undefined =>
  events.find((e) => e.event === 'created' && e.actor)?.actor

// List-cell date: "Jan 15, 2024" (drawer adds the time: "Jan 15, 2024, 10:02 AM").
// Fixed en-US pattern so the output never depends on ICU's per-locale month
// punctuation. Raw value when it does not parse; "—" when empty.
export const formatListDate = (
  value?: string | null,
  withTime = false,
): string => {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...(withTime ? { hour: 'numeric', minute: '2-digit', hour12: true } : {}),
  })
}

// Detail-drawer metrics (mockup `.d-metrics` + Performance tab): counts plus
// whole-number rates. Delivery is over sends; open/click/conversion are over
// deliveries, falling back to sends when delivery is untracked. null = "—".
export interface EngagementRates {
  sent: number
  delivered: number
  opens: number
  clicks: number
  conversions: number
  revenue: number | null
  deliveryRate: number | null
  openRate: number | null
  clickRate: number | null
  conversionRate: number | null
}

export const engagementRates = (
  stats: CampaignStats | undefined,
): EngagementRates => {
  const sent = stats?.sent ?? 0
  const delivered = stats?.delivered ?? 0
  const base = delivered || sent
  return {
    sent,
    delivered,
    opens: stats?.opens ?? 0,
    clicks: stats?.clicks ?? 0,
    conversions: stats?.conversions ?? 0,
    revenue: stats?.revenue ?? null,
    deliveryRate:
      stats?.delivered === undefined ? null : wholePercent(delivered, sent),
    openRate: sent ? wholePercent(stats?.opens, base) : null,
    clickRate: sent ? wholePercent(stats?.clicks, base) : null,
    conversionRate: sent ? wholePercent(stats?.conversions, base) : null,
  }
}

// ── Count + percentage cells, list summary, date filter, sort ───────────────

// A tracked metric: its raw count, whole-number pct (null when the
// denominator is 0) and the `count · pct%` label shown everywhere.
export interface RateCell {
  count: number
  pct: number | null
  label: string
}

const rateCell = (count: number, denominator: number): RateCell => {
  const pct = wholePercent(count, denominator)
  return {
    count,
    pct,
    label: `${count.toLocaleString('en-US')} · ${pct === null ? '—' : `${pct}%`}`,
  }
}

// Delivered over sent; opens / clicks / unsubscribes / conversions over
// deliveries, falling back to sends when delivered is 0 or untracked.
export const emailRates = (
  stats?: CampaignStats,
): {
  delivered: RateCell
  opens: RateCell
  clicks: RateCell
  unsubscribes: RateCell
  conversions: RateCell
} => {
  const sent = stats?.sent ?? 0
  const base = stats?.delivered || sent
  return {
    delivered: rateCell(stats?.delivered ?? 0, sent),
    opens: rateCell(stats?.opens ?? 0, base),
    clicks: rateCell(stats?.clicks ?? 0, base),
    unsubscribes: rateCell(stats?.unsubscribes ?? 0, base),
    conversions: rateCell(stats?.conversions ?? 0, base),
  }
}

export interface ListSummary {
  total: number
  active: number
  scheduled: number
  drafts: number
  reach: number
  avgOpenRate: number | null
  revenue: number
}

// Stats strip of the Campaigns list. Avg open rate is the mean of
// opens / (delivered || sent) over EMAIL campaigns that sent something —
// SMS has no open tracking and would drag the mean down.
export const listSummary = (
  campaigns: LoyaltyCampaign[],
  flows: LoyaltyFlow[],
): ListSummary => {
  const openRates: number[] = []
  for (const c of campaigns) {
    if (campaignChannel(c) === 'sms') continue
    const sent = c.stats?.sent ?? 0
    if (sent <= 0) continue
    openRates.push((c.stats?.opens ?? 0) / (c.stats?.delivered || sent))
  }
  const all = [...campaigns, ...flows]
  return {
    total: all.length,
    active:
      campaigns.filter(
        (c) => c.status === 'preparing' || c.status === 'sending',
      ).length + flows.filter((f) => f.status === 'live').length,
    scheduled: campaigns.filter((c) => c.status === 'scheduled').length,
    drafts:
      campaigns.filter((c) => c.status === 'draft').length +
      flows.filter((f) => f.status === 'draft').length,
    reach: all.reduce((sum, r) => sum + (r.stats?.sent ?? 0), 0),
    avgOpenRate: openRates.length
      ? Math.round(
          (openRates.reduce((a, b) => a + b, 0) / openRates.length) * 100,
        )
      : null,
    revenue: all.reduce((sum, r) => sum + (r.stats?.revenue ?? 0), 0),
  }
}

// Bulk-delete note for the in-flight campaigns the server refuses to delete.
export const bulkDeleteSkippedNote = (
  skipped: number,
  anyPreparing: boolean,
): string =>
  anyPreparing
    ? `${skipped} ${skipped === 1 ? 'campaign that is' : 'campaigns that are'} sending or preparing ${skipped === 1 ? 'is' : 'are'} skipped — they can't be deleted mid-send.`
    : `${skipped} sending ${skipped === 1 ? 'campaign is' : 'campaigns are'} skipped — they can't be deleted mid-send.`

export type DateRange =
  | 'all'
  | '7'
  | '30'
  | '90'
  | 'year'
  | { from: string; to: string }

const DAY_MS = 86_400_000

// Date filter over `sent_at || scheduled_at || created_at` (undated drafts fall
// back to creation). Rows with no date are only
// kept for 'all'. Custom range bounds are inclusive local YYYY-MM-DD days
// (an empty bound is open).
export const filterByDate = <
  T extends {
    sent_at?: string | null
    scheduled_at?: string | null
    created_at?: string | null
  },
>(
  rows: T[],
  range: DateRange,
  now: Date = new Date(),
): T[] => {
  if (range === 'all') return rows
  let min = Number.NEGATIVE_INFINITY
  let max = Number.POSITIVE_INFINITY
  if (range === 'year') {
    min = new Date(now.getFullYear(), 0, 1).getTime()
  } else if (typeof range === 'string') {
    min = now.getTime() - Number(range) * DAY_MS
  } else {
    if (range.from) min = new Date(`${range.from}T00:00:00`).getTime()
    if (range.to) max = new Date(`${range.to}T23:59:59.999`).getTime()
  }
  return rows.filter((row) => {
    const iso = row.sent_at || row.scheduled_at || row.created_at
    if (!iso) return false
    const at = new Date(iso).getTime()
    if (Number.isNaN(at)) return false
    return at >= min && at <= max
  })
}

export type SortKey = 'newest' | 'oldest' | 'open_rate' | 'revenue'

type SortableRow =
  | { kind: 'campaign'; sortAt: number; campaign: LoyaltyCampaign }
  | { kind: 'flow'; sortAt: number; flow: LoyaltyFlow }

const rowStats = (row: SortableRow): CampaignStats | undefined =>
  row.kind === 'campaign' ? row.campaign.stats : row.flow.stats

export const sortCampaignRows = <R extends SortableRow>(
  rows: R[],
  key: SortKey,
): R[] => {
  const value = (row: R): number => {
    switch (key) {
      case 'newest':
        return row.sortAt
      case 'oldest':
        // Undated rows (sortAt 0) sort last, not first.
        return row.sortAt > 0 ? -row.sortAt : Number.NEGATIVE_INFINITY
      case 'revenue':
        return rowStats(row)?.revenue ?? 0
      default:
        return emailRates(rowStats(row)).opens.pct ?? -1
    }
  }
  return [...rows].sort((a, b) => value(b) - value(a))
}

// ── Campaign history (detail drawer Activity tab) ────────────────────────────

// Status-pill copy shared by the pills and the Activity timeline (the
// component re-exports it): Draft / Scheduled / Preparing / Active / Paused /
// Completed / Failed.
export const STATUS_PILL_LABELS: Record<CampaignDisplayStatus, string> = {
  draft: 'Draft',
  scheduled: 'Scheduled',
  preparing: 'Preparing',
  running: 'Active',
  paused: 'Paused',
  completed: 'Completed',
  failed: 'Failed',
}

const historyStatusLabel = (status: CampaignStatus | '' | undefined): string =>
  status ? STATUS_PILL_LABELS[displayStatusForCampaign({ status })] : '—'

// Friendly names for the content fields a `content_updated` row lists.
const CONTENT_FIELD_LABELS: Record<string, string> = {
  name: 'name',
  subject: 'subject',
  preview_text: 'preview text',
  body_html: 'body',
  cta_text: 'button text',
  cta_url: 'button link',
  logo_url: 'logo',
  coupon_code: 'coupon',
  from_email: 'from email',
  from_label: 'from name',
  sms_body: 'SMS body',
  ends_at: 'end date',
  audience: 'audience',
  reward: 'reward',
  content_mode: 'Content type',
  blocks: 'Email blocks',
  email_settings: 'Email design',
  compiled_html: 'Email content',
  template_key: 'Template',
}

const detailString = (
  detail: Record<string, unknown> | undefined,
  key: string,
): string | undefined => {
  const value = detail?.[key]
  return typeof value === 'string' && value ? value : undefined
}

const detailNumber = (
  detail: Record<string, unknown> | undefined,
  key: string,
): number | undefined => {
  const value = detail?.[key]
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined
}

// Local "Jan 15, 2026, 10:02 AM" for a scheduled_at ISO string; the raw
// string when it does not parse.
export const formatHistoryDateTime = (iso: string): string => {
  const parsed = new Date(iso)
  if (Number.isNaN(parsed.getTime())) return iso
  return parsed.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

export interface CampaignHistoryLine {
  // Event line, e.g. "Status changed: Draft → Scheduled · by Jane".
  text: string
  // Optional second line rendered from `detail`, e.g. "Sent 2,847 · Failed 3".
  sub?: string
}

// Event line + optional detail line for one campaign-history row. `detail`
// is always an object on the wire (never a string — rendering it directly
// would crash React), so every known shape is formatted here:
// `{scheduled_at}` → "Scheduled for <local datetime>", `{send: 'now'}` →
// "Send now", `{fields}` → "Updated: subject, body…", `{recipients}` →
// "N recipients", `{sent, failed}` → "Sent N · Failed M", `{reason}` → the
// reason. Unknown events fall back to their raw event name. The date
// formatter is injectable for tests (locale/timezone independence).
export const describeCampaignHistoryEvent = (
  event: Pick<
    LoyaltyCampaignHistoryEvent,
    'event' | 'from_status' | 'to_status' | 'actor' | 'detail'
  >,
  formatDateTime: (iso: string) => string = formatHistoryDateTime,
): CampaignHistoryLine => {
  const detail =
    event.detail && typeof event.detail === 'object' ? event.detail : undefined
  const byActor = event.actor ? ` · by ${event.actor}` : ''
  const sendSummary = (): string | undefined => {
    const sent = detailNumber(detail, 'sent')
    const failed = detailNumber(detail, 'failed')
    if (sent === undefined && failed === undefined) return undefined
    return `Sent ${(sent ?? 0).toLocaleString()} · Failed ${(failed ?? 0).toLocaleString()}`
  }

  switch (event.event) {
    case 'created':
      return { text: `Campaign created${byActor}` }
    case 'status_changed': {
      const text = `Status changed: ${historyStatusLabel(event.from_status)} → ${historyStatusLabel(event.to_status)}${byActor}`
      const scheduledAt = detailString(detail, 'scheduled_at')
      if (scheduledAt)
        return { text, sub: `Scheduled for ${formatDateTime(scheduledAt)}` }
      if (detailString(detail, 'send') === 'now')
        return { text, sub: 'Send now' }
      return { text }
    }
    case 'content_updated': {
      const fields = Array.isArray(detail?.fields)
        ? detail.fields.filter((f): f is string => typeof f === 'string')
        : []
      const text = `Content updated${byActor}`
      if (fields.length === 0) return { text }
      return {
        text,
        sub: `Updated: ${fields.map((f) => CONTENT_FIELD_LABELS[f] ?? f).join(', ')}`,
      }
    }
    case 'send_started': {
      const recipients = detailNumber(detail, 'recipients')
      return {
        text: 'Send started',
        sub:
          recipients === undefined
            ? undefined
            : `${recipients.toLocaleString()} ${recipients === 1 ? 'recipient' : 'recipients'}`,
      }
    }
    case 'send_completed':
      return { text: 'Send completed', sub: sendSummary() }
    case 'send_failed':
      return {
        text: 'Send failed',
        sub: detailString(detail, 'reason') ?? sendSummary(),
      }
    case 'send_on_hold':
      return {
        text: 'Sending on hold',
        sub: detailString(detail, 'note') ?? detailString(detail, 'reason'),
      }
    case 'send_resumed':
      return { text: 'Sending resumed' }
    default:
      return { text: `${event.event}${byActor}` }
  }
}
